/**
 * ScoutingReportsListContainer - Manages scouting reports state
 *
 * Container component that handles state and callbacks for the scouting
 * reports list using real player data from GraphQL. Opens a report modal
 * directly when a report is clicked, and navigates to the matches page
 * for report creation. Refetches PlayerDetailGetPlayer when the report
 * modal closes so that edits made via the Form.io WebView are reflected
 * in the Apollo cache and downstream components (e.g. AI Summary) get
 * fresh gesture handlers.
 *
 * Distinguishes a failed reports read from a player who has none (SE-7872).
 * The reports arrive as a nullable field of the page's player read, and
 * `?? []` used to turn the failed-read `null` into an empty array — so the
 * card said "No scouting reports yet" whether the player had none or the read
 * had not come back. The empty copy is unchanged; it simply no longer doubles
 * as the failure state. See `utils/reportsNotesReadState.ts` for why `null`
 * can only mean failure here.
 */

import { useApolloClient } from "@apollo/client";
import { Href, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";

import ScoutingReportsListView from "./ScoutingReportsListView";

import type { ScoutingReport } from "@/features/player-detail/types";
import {
  getScoutReportDisplayText,
  getScoutReportType,
} from "@/features/player-detail/utils/scoutingReportUtils";
import { deriveScoutReportsReadState } from "@/features/player-detail/utils/reportsNotesReadState";

import ReportItem from "@/features/reports/components/organisms/ReportItem";
import type { PlayerDetailScoutReportsFragment } from "@/generated/graphql";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import { useTheme } from "@/providers/ThemeProvider";
import { playerPreviewOpenVar } from "@/stores/player";
import { useAppLocale } from "@/i18n";
import {
  MEDIUM_DATE_OPTIONS,
  formatDate as formatIntlDate,
} from "@/utils/intlFormat";

/** Maximum number of reports to display before showing "View all" */
const MAX_VISIBLE_REPORTS = 4;

/**
 * Props for ScoutingReportsListContainer component
 */
interface ScoutingReportsListContainerProps {
  /** Scout reports from player data */
  readonly scoutReports?: readonly PlayerDetailScoutReportsFragment[] | null;
  /** Player ID for creating new reports */
  readonly playerId?: string;
  /** Player's display name for PDF filename generation (SE-4257) */
  readonly playerName?: string;
  /**
   * Whether the card is one of two side-by-side columns rather than a block in
   * a stack. Threaded straight to the view, which uses it to decide whether the
   * card may take a zero flex basis (SE-6910).
   */
  readonly isSideBySide?: boolean;
}

/**
 * Format date string to display format, in the active UI locale.
 *
 * SE-7966: the locale was hardcoded "en-US".
 * @param dateString - ISO date string or null
 * @param locale - The active UI locale
 * @returns Formatted date string
 */
const formatReportDate = (
  dateString: string | null | undefined,
  locale: string
): string => {
  if (!dateString) return "Unknown date";
  return formatIntlDate(dateString, locale, MEDIUM_DATE_OPTIONS) || dateString;
};

/**
 * Derive a stable ID for a scout report, using reportId with ggPlayerKey as fallback
 * @param report - GraphQL scout report fragment
 * @returns Report identifier string
 */
const getScoutReportId = (report: PlayerDetailScoutReportsFragment): string =>
  report.reportId ?? report.ggPlayerKey ?? "";

/**
 * Transform GraphQL scout report to display format
 * @param report - GraphQL scout report fragment
 * @param locale - The active UI locale, for the report date
 * @returns Formatted scouting report for display
 */
const transformScoutReport = (
  report: PlayerDetailScoutReportsFragment,
  locale: string
): ScoutingReport => {
  const displayText = getScoutReportDisplayText(report);
  return {
    id: getScoutReportId(report),
    date: formatReportDate(report.reportDate, locale),
    displayText,
    reportType: getScoutReportType(displayText),
    author: report.scoutName ?? "Unknown Scout",
    canEdit: report.canEdit,
  };
};

/**
 * ScoutingReportsListContainer manages state for scouting reports list
 * @param props - Container props
 * @param props.scoutReports - Scout reports from player data
 * @param props.playerId - Player ID for creating new reports
 * @param props.playerName - Player's display name for PDF filename generation (SE-4257)
 * @param props.isSideBySide - Whether the card is one of two side-by-side columns
 * @returns ScoutingReportsListView with state and callbacks
 */
const ScoutingReportsListContainer = ({
  scoutReports,
  playerId,
  playerName,
  isSideBySide,
}: ScoutingReportsListContainerProps) => {
  const locale = useAppLocale();
  const client = useApolloClient();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const router = useRouter();
  const matchesUrl = useExpandedPlayerUrl(`/players/${playerId}/matches`);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  const selectedReport = useMemo(
    () =>
      selectedReportId
        ? (scoutReports?.find(r => getScoutReportId(r) === selectedReportId) ??
          null)
        : null,
    [selectedReportId, scoutReports]
  );

  const allReports = useMemo(
    (): readonly ScoutingReport[] =>
      scoutReports?.map(report => transformScoutReport(report, locale)) ?? [],
    [scoutReports, locale]
  );

  // SE-7872: the `?? []` above still flattens null to nothing for RENDERING,
  // but the distinction it used to destroy is preserved here and handed to the
  // view. `null` is a failed field, `[]` is a player with no reports, and the
  // card must not say the second when it means the first.
  const readState = useMemo(
    () => deriveScoutReportsReadState(scoutReports),
    [scoutReports]
  );

  const [isRetrying, setIsRetrying] = useState(false);

  /**
   * Retries the read behind this card. The reports are a field of the page's
   * `PlayerDetailGetPlayer` read rather than a query this card owns, so the
   * retry refetches that — the same call the report modal already makes on
   * close. A second failure leaves the error body on screen, which is the
   * intended outcome.
   */
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    void client
      .refetchQueries({ include: ["PlayerDetailGetPlayer"] })
      .catch(error => {
        console.warn(
          "[ScoutingReportsList]",
          "Failed to refetch player data while retrying a failed reports read",
          { error: error instanceof Error ? error.message : error }
        );
      })
      .finally(() => {
        setIsRetrying(false);
      });
  }, [client]);

  const visibleReports = useMemo(
    () => allReports.slice(0, MAX_VISIBLE_REPORTS),
    [allReports]
  );

  const hasMoreReports = allReports.length > MAX_VISIBLE_REPORTS;
  const [showViewAllModal, setShowViewAllModal] = useState(false);

  const handleAddReport = useCallback(() => {
    // When shown inside the preview modal (dismissed on navigation), flag the
    // Select Matches screen to drop its back arrow so it doesn't dead-end.
    const url = playerPreviewOpenVar()
      ? `${matchesUrl}?hideBackButton=true`
      : matchesUrl;
    router.push(url as Href);
  }, [router, matchesUrl]);

  const handleReportPress = useCallback((reportId: string) => {
    setShowViewAllModal(false);
    setSelectedReportId(reportId);
  }, []);

  const handleReportModalClose = useCallback(() => {
    setSelectedReportId(null);
    void client
      .refetchQueries({ include: ["PlayerDetailGetPlayer"] })
      .catch(error => {
        console.warn(
          "[ScoutingReportsList]",
          "Failed to refetch player data after closing report modal",
          { error: error instanceof Error ? error.message : error }
        );
      });
  }, [client]);

  const handleViewAll = useCallback(() => {
    setShowViewAllModal(true);
  }, []);

  const handleCloseViewAll = useCallback(() => {
    setShowViewAllModal(false);
  }, []);

  return (
    <>
      <ScoutingReportsListView
        isDark={isDark}
        isSideBySide={isSideBySide}
        reports={visibleReports}
        allReports={allReports}
        reportCount={allReports.length}
        hasMoreReports={hasMoreReports}
        showViewAllModal={showViewAllModal}
        onAddReport={handleAddReport}
        onReportPress={handleReportPress}
        onViewAll={handleViewAll}
        onCloseViewAll={handleCloseViewAll}
        readState={readState}
        onRetry={handleRetry}
        isRetrying={isRetrying}
      />
      {selectedReport ? (
        <ReportItem
          item={selectedReport}
          playerName={playerName}
          defaultOpen
          onClose={handleReportModalClose}
        />
      ) : null}
    </>
  );
};

export default ScoutingReportsListContainer;
